import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { SubmissionData } from '../../../types';
import { SubmissionStatus, SubmissionType } from '../../../enums';
import { asArray } from '../../../utils/asArray';
import { BrandChip } from '../../common/BrandChip';
import { SubmissionCode } from '../../common/SubmissionCode';
import { resolveBrandName } from '../../../utils/brandLabel';
import {
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Lightbulb,
  Megaphone,
  Search,
  ShieldAlert,
  Tag,
  TrendingUp,
  User,
  X
} from 'lucide-react';
import { apiClient } from '../../../services/apiClient';

interface ManagerDashboardViewProps {
  submissions: SubmissionData[];
  managerId: string;
  managerName: string;
  isAdminView?: boolean;
  adminViewerName?: string;
  canCreate?: boolean;
  onNewSubmission?: () => void;
  onNewProposal?: () => void;
  onOpenMySubmissions?: () => void;
  onExitAdminView?: () => void;
  loading?: boolean;
  onOpenSubmissionDetail?: (submissionId: string, type?: SubmissionType) => void;
  canViewHistory?: boolean;
  initialTab?: 'overview' | 'history';
  onHistoryNavigate?: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const renderBrandChip = (sub: SubmissionData) => <BrandChip brandName={resolveBrandName(sub)} />;

const statusClassMap: Record<SubmissionStatus, string> = {
  [SubmissionStatus.PENDING]: 'bg-amber-50 text-amber-700 border border-amber-200',
  [SubmissionStatus.NEEDS_REVISION]: 'bg-amber-50 text-amber-700 border border-amber-200',
  [SubmissionStatus.APPROVED]: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  [SubmissionStatus.EXPORTED]: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  [SubmissionStatus.REJECTED]: 'bg-rose-50 text-rose-700 border border-rose-200'
};

export const ManagerDashboardView: React.FC<ManagerDashboardViewProps> = ({
  submissions,
  managerId,
  managerName,
  isAdminView = false,
  adminViewerName,
  canCreate = false,
  onNewSubmission,
  onNewProposal,
  onOpenMySubmissions,
  onExitAdminView,
  loading = false,
  onOpenSubmissionDetail,
  canViewHistory = false,
  initialTab,
  onHistoryNavigate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const [historySearch, setHistorySearch] = useState('');
  const [historyStart, setHistoryStart] = useState<Date | null>(null);
  const [historyEnd, setHistoryEnd] = useState<Date | null>(null);
  const campaignBrandsCache = useRef<Record<string, { loading: boolean; names: string[]; error?: boolean }>>({});
  const [historyType, setHistoryType] = useState<'all' | 'campaign' | 'proposal'>('all');
  const initialTabApplied = useRef(false);

  useEffect(() => {
    if (!initialTabApplied.current && initialTab && canViewHistory) {
      setActiveTab(initialTab);
      initialTabApplied.current = true;
    }
  }, [initialTab, canViewHistory]);

  useEffect(() => {
    if (!canViewHistory && activeTab === 'history') {
      setActiveTab('overview');
    }
  }, [activeTab, canViewHistory]);

  const safeSubmissions = useMemo(() => {
    const raw = asArray(submissions);
    const cleaned = raw.filter((x): x is SubmissionData => 
      Boolean(x) && typeof x === 'object' && 'submissionId' in x
    );
    if (cleaned.length !== raw.length) {
      console.warn(`ManagerDashboardView: Removed ${raw.length - cleaned.length} invalid submission entries`);
    }
    return cleaned;
  }, [submissions]);

  const scopedSubmissions = useMemo(
    () =>
      safeSubmissions.filter(
        (s) => s.managerId === managerId || s.manager?.fileNumber === managerId
      ),
    [safeSubmissions, managerId]
  );

  const filteredSubmissions = useMemo(() => {
    if (!searchTerm.trim()) return scopedSubmissions;
    const term = searchTerm.toLowerCase();
    return scopedSubmissions.filter((s) => {
      const matchesId = s.submissionId?.toLowerCase().includes(term);
      const matchesCampaign = s.campaignName?.toLowerCase().includes(term);
      const matchesBrand = asArray(s.brands).some((b) => (b || '').toString().toLowerCase().includes(term));
      return matchesId || matchesCampaign || matchesBrand;
    });
  }, [scopedSubmissions, searchTerm]);

  const ensureCampaignBrands = async (campaignId: string) => {
    if (!campaignId) return;
    if (campaignBrandsCache.current[campaignId]) return;
    campaignBrandsCache.current[campaignId] = { loading: true, names: [], error: false };
    try {
      const res = await apiClient.campaigns.getBrands(campaignId);
      const names = (res?.brands || []).map((b: any) => b.name).filter(Boolean);
      campaignBrandsCache.current[campaignId] = { loading: false, names, error: false };
      setSearchTerm((prev) => prev); // force render
    } catch (e) {
      campaignBrandsCache.current[campaignId] = { loading: false, names: [], error: true };
      setSearchTerm((prev) => prev);
    }
  };

  useEffect(() => {
    const ids = new Set<string>();
    scopedSubmissions.forEach((s) => {
      const cid = (s as any).campaignId;
      if (cid) ids.add(cid);
    });
    ids.forEach((id) => ensureCampaignBrands(id));
  }, [scopedSubmissions]);

  const sortedSubmissions = useMemo(
    () =>
      [...filteredSubmissions].sort(
        (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
      ),
    [filteredSubmissions]
  );

  const totals = useMemo(() => {
    const campaigns = scopedSubmissions.filter((s) => s?.type !== SubmissionType.PROPOSAL).length;
    const proposals = scopedSubmissions.filter((s) => s?.type === SubmissionType.PROPOSAL).length;
    const pending = scopedSubmissions.filter(
      (s) => s.status === SubmissionStatus.PENDING || s.status === SubmissionStatus.NEEDS_REVISION
    ).length;
    const approved = scopedSubmissions.filter(
      (s) => s.status === SubmissionStatus.APPROVED || s.status === SubmissionStatus.EXPORTED
    ).length;
    const rejected = scopedSubmissions.filter((s) => s.status === SubmissionStatus.REJECTED).length;
    const items = scopedSubmissions.reduce((acc, curr) => acc + asArray(curr?.items).length, 0);
    return {
      campaigns,
      proposals,
      pending,
      approved,
      rejected,
      items,
      total: scopedSubmissions.length
    };
  }, [scopedSubmissions]);

  const brandLeaders = useMemo(() => {
    const counts: Record<string, number> = {};
    scopedSubmissions.forEach((s) => {
      asArray(s.brands).forEach((b) => {
        const key = b || 'Unknown';
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  }, [scopedSubmissions]);

  const statusBreakdown = useMemo(() => {
    const keys: SubmissionStatus[] = [
      SubmissionStatus.PENDING,
      SubmissionStatus.NEEDS_REVISION,
      SubmissionStatus.APPROVED,
      SubmissionStatus.EXPORTED,
      SubmissionStatus.REJECTED
    ];
    const totalsByStatus = keys.map((status) => ({
      status,
      count: scopedSubmissions.filter((s) => s.status === status).length
    }));
    const max = Math.max(...totalsByStatus.map((s) => s.count), 1);
    return totalsByStatus.map((row) => ({
      ...row,
      percent: Math.round((row.count / max) * 100)
    }));
  }, [scopedSubmissions]);

  const renderAppliesLine = (sub: SubmissionData) => {
    const cid = (sub as any).campaignId;
    if (cid) {
      const entry = campaignBrandsCache.current[cid];
      if (!entry || entry.loading) {
        return <div className="text-[11px] text-zinc-500">Applies to: Loading...</div>;
      }
      if (entry.error) {
        return <div className="text-[11px] text-zinc-500">Applies to: unavailable</div>;
      }
      const names = entry.names || [];
      const display = names.slice(0, 3).join(', ');
      const extra = names.length > 3 ? ` +${names.length - 3} more` : '';
      return (
        <div className="text-[11px] text-zinc-500">
          Applies to: <span className="font-semibold text-zinc-700">{display || 'â€”'}{extra}</span>
        </div>
      );
    }
    if (sub.campaignName) {
      return (
        <div className="text-[11px] text-zinc-500">
          Campaign: <span className="font-semibold text-zinc-700">{sub.campaignName}</span>{' '}
          <span className="text-amber-600 font-semibold">(not linked)</span>
        </div>
      );
    }
    return null;
  };

  const needsRevisionSubmissions = useMemo(
    () =>
      scopedSubmissions
        .filter((s) => s.status === SubmissionStatus.NEEDS_REVISION)
        .sort(
          (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
        ),
    [scopedSubmissions]
  );

  const filteredHistory = useMemo(() => {
    return scopedSubmissions
      .filter((s) => {
        const term = historySearch.toLowerCase();
        const matchesTerm =
          !term ||
          s.submissionId?.toLowerCase().includes(term) ||
          s.campaignName?.toLowerCase().includes(term) ||
          asArray(s.brands).some((b) => (b || '').toString().toLowerCase().includes(term));
        const startOk = historyStart ? new Date(s.timestamp || 0) >= historyStart : true;
        const endOk = historyEnd ? new Date(s.timestamp || 0) <= historyEnd : true;
        const typeOk =
          historyType === 'all' ||
          (historyType === 'proposal' && s.type === SubmissionType.PROPOSAL) ||
          (historyType === 'campaign' && s.type !== SubmissionType.PROPOSAL);
        return matchesTerm && startOk && endOk && typeOk;
      })
      .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
  }, [scopedSubmissions, historySearch, historyStart, historyEnd, historyType]);

  const renderTypeBadge = (type?: SubmissionType) => {
    if (type === SubmissionType.PROPOSAL) {
      return (
        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
          <Lightbulb className="w-3 h-3" /> Proposal
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
        <Megaphone className="w-3 h-3" /> Campaign
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {isAdminView && (
        <div className="border-2 border-amber-200 bg-amber-50 rounded-2xl p-4 flex items-start gap-3 shadow-sm">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800">
              Viewing Manager Dashboard for: {managerName}
            </p>
            <p className="text-xs text-amber-700">
              {adminViewerName ? `Opened as ${adminViewerName}. ` : ''}
              Data is scoped to this manager only.
            </p>
          </div>
          {onExitAdminView && (
            <button
              onClick={onExitAdminView}
              className="text-xs font-bold text-amber-800 hover:text-amber-900 bg-white border border-amber-200 px-3 py-1.5 rounded-lg"
            >
              Exit View
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Manager Dashboard</h2>
          {canViewHistory && (
            <div className="mt-3 inline-flex bg-zinc-100 rounded-full p-1 text-xs font-bold text-zinc-500">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-3 py-1 rounded-full transition ${
                  activeTab === 'overview' ? 'bg-white text-zinc-900 shadow-sm' : ''
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-3 py-1 rounded-full transition ${
                  activeTab === 'history' ? 'bg-white text-zinc-900 shadow-sm' : ''
                }`}
              >
                History
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center justify-end">
          {loading && (
            <span className="text-xs font-bold text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-lg px-3 py-1">
              Refreshing data...
            </span>
          )}
          {onOpenMySubmissions && (
            <button
              onClick={onOpenMySubmissions}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 text-zinc-700 rounded-xl font-bold text-sm shadow-sm hover:bg-zinc-50"
            >
              <FileText className="w-4 h-4" /> My Submissions
            </button>
          )}
          {canCreate && onNewProposal && (
            <button
              onClick={onNewProposal}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm shadow-md hover:bg-indigo-700"
            >
              <Lightbulb className="w-4 h-4" /> New Proposal
            </button>
          )}
          {canCreate && onNewSubmission && (
            <button
              onClick={onNewSubmission}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl font-bold text-sm shadow-md hover:bg-teal-700"
            >
              <FileText className="w-4 h-4" /> New Submission
            </button>
          )}
        </div>
      </div>

      {activeTab === 'history' && canViewHistory ? (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-zinc-700">
              <Clock className="w-4 h-4" /> Submission History
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl">
                <Search className="w-4 h-4 text-zinc-400" />
                <input
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search submissions, campaigns, brands"
                  className="bg-transparent text-sm outline-none w-48 md:w-64"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-xl">
                <Calendar className="w-4 h-4 text-zinc-400" />
                <ReactDatePicker
                  selected={historyStart}
                  onChange={(date: Date | null) => setHistoryStart(date)}
                  placeholderText="mm/dd/yyyy"
                  dateFormat="MM/dd/yyyy"
                  className="min-w-[8.5rem] bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 hover:border-teal-300 rounded-lg px-3 py-2 text-sm font-medium text-zinc-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                  calendarClassName="custom-datepicker"
                  maxDate={historyEnd || undefined}
                  selectsStart
                  startDate={historyStart}
                  endDate={historyEnd}
                />
                <span className="text-zinc-400 text-xs">to</span>
                <ReactDatePicker
                  selected={historyEnd}
                  onChange={(date: Date | null) => setHistoryEnd(date)}
                  placeholderText="mm/dd/yyyy"
                  dateFormat="MM/dd/yyyy"
                  className="min-w-[8.5rem] bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 hover:border-teal-300 rounded-lg px-3 py-2 text-sm font-medium text-zinc-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
                  calendarClassName="custom-datepicker"
                  minDate={historyStart || undefined}
                  selectsEnd
                  startDate={historyStart}
                  endDate={historyEnd}
                />
              </div>
              <select
                value={historyType}
                onChange={(e) => setHistoryType(e.target.value as 'all' | 'campaign' | 'proposal')}
                className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-sm font-bold text-zinc-700"
              >
                <option value="all">All</option>
                <option value="campaign">Campaigns</option>
                <option value="proposal">Proposals</option>
              </select>
            </div>
          </div>

          <div className="divide-y divide-zinc-100 rounded-xl border border-zinc-100">
            {filteredHistory.length === 0 && (
              <div className="p-6 text-sm text-zinc-500">No submissions match your filters.</div>
            )}
            {filteredHistory.map((sub) => (
              <button
                key={sub.submissionId || Math.random().toString(36)}
                onClick={() => {
                  if (onHistoryNavigate) onHistoryNavigate();
                  if (onOpenSubmissionDetail && sub.submissionId) {
                    onOpenSubmissionDetail(sub.submissionId, sub.type);
                  } else {
                    setSelectedSubmission(sub);
                  }
                }}
                className="w-full text-left p-4 hover:bg-zinc-50 transition flex flex-col gap-1"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {renderTypeBadge(sub.type)}
                    <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                    <BrandChip brandName={resolveBrandName(sub)} />
                  </div>
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      sub.status
                        ? statusClassMap[sub.status]
                        : 'bg-zinc-50 text-zinc-600 border border-zinc-200'
                    }`}
                  >
                    {sub.status || 'Unknown'}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                  <span className="font-bold text-zinc-700">{sub.campaignName || 'Untitled'}</span>
                  <span className="text-zinc-300">â€¢</span>
                  <span>{formatDate(sub.timestamp) || 'No date'}</span>
                  <span className="text-zinc-300">â€¢</span>
                  <span>
                    {resolveBrandName(sub)} -{' '}
                    {sub.currentStage || 'Needs Revision'}
                  </span>
                </div>
                {renderAppliesLine(sub)}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {needsRevisionSubmissions.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl shadow-sm p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-sm font-bold text-amber-900">Needs Revision</p>
                      <p className="text-xs text-amber-700">Items returned for updates. Click to review.</p>
                    </div>
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-white border border-amber-200 text-amber-800">
                      {needsRevisionSubmissions.length}
                    </span>
                  </div>
                  <div className="divide-y divide-amber-100 rounded-xl border border-amber-100 overflow-hidden bg-white/50">
                    {needsRevisionSubmissions.map((sub) => (
                      <button
                        key={sub.submissionId || Math.random().toString(36)}
                        onClick={() => {
                          if (onHistoryNavigate) onHistoryNavigate();
                          if (onOpenSubmissionDetail && sub.submissionId) {
                            onOpenSubmissionDetail(sub.submissionId, sub.type);
                          } else {
                            setSelectedSubmission(sub);
                          }
                        }}
                        className="w-full text-left p-4 hover:bg-amber-50 transition flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {renderTypeBadge(sub.type)}
                            <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                            <BrandChip brandName={resolveBrandName(sub)} />
                          </div>
                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            Needs Revision
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-amber-700">
                          <span className="font-bold text-amber-900">{sub.campaignName || 'Untitled'}</span>
                          <span className="text-amber-300">→</span>
                          <span>{formatDate(sub.timestamp) || 'No date'}</span>
                          <span className="text-amber-300">→</span>
                          <span>
                            {resolveBrandName(sub)} -{' '}
                            {sub.currentStage || 'Needs Revision'}
                          </span>
                        </div>
                        {renderAppliesLine(sub)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 className="w-5 h-5 text-teal-600" />
                <p className="text-sm font-bold text-zinc-600">Total</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.total}</p>
              <p className="text-xs text-zinc-500">{totals.campaigns} campaigns â€¢ {totals.proposals} proposals</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Megaphone className="w-5 h-5 text-emerald-600" />
                <p className="text-sm font-bold text-zinc-600">Campaigns</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.campaigns}</p>
              <p className="text-xs text-zinc-500">Total campaign submissions</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Lightbulb className="w-5 h-5 text-indigo-600" />
                <p className="text-sm font-bold text-zinc-600">Proposals</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.proposals}</p>
              <p className="text-xs text-zinc-500">Total proposals</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <p className="text-sm font-bold text-zinc-600">In Review</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.pending}</p>
              <p className="text-xs text-zinc-500">Pending or needs revision</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-sm font-bold text-zinc-600">Approved</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.approved}</p>
              <p className="text-xs text-zinc-500">Approved or exported</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <ShieldAlert className="w-5 h-5 text-rose-500" />
                <p className="text-sm font-bold text-zinc-600">Rejected</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.rejected}</p>
              <p className="text-xs text-zinc-500">Rejected submissions</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-zinc-700" />
                <p className="text-sm font-bold text-zinc-600">Items</p>
              </div>
              <p className="text-3xl font-extrabold text-zinc-900">{totals.items}</p>
              <p className="text-xs text-zinc-500">Total items across submissions</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-zinc-700">
                    <TrendingUp className="w-4 h-4" /> Recent submissions
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl">
                    <Search className="w-4 h-4 text-zinc-400" />
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search submissions, campaigns, brands"
                      className="bg-transparent text-sm outline-none w-56 md:w-72"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {sortedSubmissions.slice(0, 10).map((sub) => (
                    <button
                      key={sub.submissionId || Math.random().toString(36)}
                      onClick={() => {
                        if (onHistoryNavigate && activeTab === 'history') onHistoryNavigate();
                        if (onOpenSubmissionDetail && sub.submissionId) {
                          onOpenSubmissionDetail(sub.submissionId, sub.type);
                        } else {
                          setSelectedSubmission(sub);
                        }
                      }}
                      className="w-full text-left bg-white border border-zinc-200 rounded-xl p-4 hover:border-teal-200 hover:shadow-md transition flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {renderTypeBadge(sub.type)}
                          <div>
                            <p className="text-sm font-bold text-zinc-900 leading-tight">
                              {sub.campaignName || 'Untitled'}
                            </p>
                            <p className="text-[11px] text-zinc-500 font-mono">
                              <SubmissionCode submissionId={sub.submissionId} brandName={resolveBrandName(sub)} />
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-zinc-500">
                            {formatDate(sub.timestamp) || 'No date'}
                          </span>
                          <span
                            className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                              sub.status
                                ? statusClassMap[sub.status]
                                : 'bg-zinc-50 text-zinc-600 border border-zinc-200'
                            }`}
                          >
                            {sub.status || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-zinc-500">
                        {resolveBrandName(sub)} -{' '}
                        {sub.currentStage || 'Needs Revision'}
                      </div>
                      {renderAppliesLine(sub)}
                    </button>
                  ))}
                  {sortedSubmissions.length === 0 && (
                    <div className="text-sm text-zinc-500">No submissions found.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-zinc-700 mb-4">
                  <BarChart3 className="w-4 h-4" /> Status breakdown
                </div>
                <div className="space-y-3">
                  {statusBreakdown.map((row) => (
                    <div key={row.status}>
                      <div className="flex items-center justify-between text-xs font-bold text-zinc-600 mb-1">
                        <span>{row.status}</span>
                        <span>{row.count}</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-zinc-700 mb-4">
                  <User className="w-4 h-4" /> Top brands
                </div>
                <div className="space-y-3">
                  {brandLeaders.length === 0 && (
                    <p className="text-sm text-zinc-500">No brand data yet.</p>
                  )}
                  {brandLeaders.map(([brand, count]) => (
                    <div key={brand} className="flex items-center justify-between text-sm text-zinc-700">
                      <span className="font-bold">{brand}</span>
                      <span className="text-xs bg-zinc-100 text-zinc-700 px-2 py-1 rounded-lg font-bold">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {selectedSubmission && (
        <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl border border-zinc-100 relative">
            <button
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100"
              onClick={() => setSelectedSubmission(null)}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-zinc-500" />
            </button>
            <div className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-zinc-700">
                {renderTypeBadge(selectedSubmission.type)}
                <SubmissionCode submissionId={selectedSubmission.submissionId} brandName={resolveBrandName(selectedSubmission)} />
                <BrandChip brandName={resolveBrandName(selectedSubmission)} />
              </div>
              <h3 className="text-xl font-bold text-zinc-900">
                {selectedSubmission.campaignName || 'Untitled Submission'}
              </h3>
                            <p className="text-sm text-zinc-500">
                {resolveBrandName(selectedSubmission)} - {selectedSubmission.currentStage || 'Needs Revision'}
              </p>
              {renderAppliesLine(selectedSubmission)}
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <Tag className="w-4 h-4" />
                <span>{formatDate(selectedSubmission.timestamp) || 'No date'}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span
                  className={`px-3 py-1 rounded-full font-bold ${
                    selectedSubmission.status
                      ? statusClassMap[selectedSubmission.status]
                      : 'bg-zinc-50 text-zinc-600 border border-zinc-200'
                  }`}
                >
                  {selectedSubmission.status || 'Unknown'}
                </span>
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end">
              <button
                onClick={() => {
                      if (onHistoryNavigate && activeTab === 'history') onHistoryNavigate();
                  if (selectedSubmission.submissionId && onOpenSubmissionDetail) {
                    onOpenSubmissionDetail(selectedSubmission.submissionId, selectedSubmission.type);
                    setSelectedSubmission(null);
                  } else {
                    setSelectedSubmission(null);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 shadow-sm"
              >
                View full submission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
